import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, IconButton, InputAdornment, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Theme, Typography, CircularProgress, useTheme,
} from '@mui/material';
import { Delete, Edit, FileUpload, LocalShipping, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { EquipmentUnit, EquipmentUnitFull } from '../../types';
import { equipmentApi } from '../../services/api';
import { extractApiError } from '../../apiUnwrapper/apiUnwrapper';

import EquipmentFormModel from '../../components/AddNewEquipmentPopUp';
import EquipmentTypeFormModel from '../../components/ManageEquipmentTypesPopUp';
import ManageEquipmentModelsPopUp from '../../components/ManageEquipmentModelsPopUp';
import ConfirmDialog from '../../components/ConfirmDialog';

import { useEquipmentData } from '../../hooks/UseEquipmentData';
import { useBulkUpload } from '../../hooks/Usebulkupload';
import { useConfirmDialog } from '../../hooks/UseConfirmDialog';
import {
  gradientButtonSx, PageHeader, StatusBanner, UploadProgressBar, EmptyTableRow,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** null = add mode, defined = edit mode */
type EditTarget = EquipmentUnitFull | null;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EquipmentsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageEquipment = user?.roleName === 'Admin';
  const { enqueueSnackbar } = useSnackbar();

  // ── Equipment units (the only data this page owns) ────────────────────────
  const {
    equipmentList, meta, loading, error, setError,
    loadEquipments, searchEquipments,
  } = useEquipmentData();

  // ── Upload ────────────────────────────────────────────────────────────────
  const { uploadState, handleFileChange, handleUpload } = useBulkUpload((file) => equipmentApi.bulkUploadEquipments(file),
    () => loadEquipments(meta.page, meta.size)
  );

  // ── Confirm dialog ────────────────────────────────────────────────────────
  const { confirmState, confirm, closeConfirm, executeConfirm } = useConfirmDialog();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]           = useState('');
  const [editTarget, setEditTarget]           = useState<EditTarget>(null);
  const [equipmentFormOpen, setEquipmentFormOpen] = useState(false);
  const [typesDialogOpen, setTypesDialogOpen] = useState(false);
  const [modelsDialogOpen, setModelsDialogOpen] = useState(false);
  const [fieldErrors, setFieldErrors]         = useState<Record<string, string>>({});

  const isEditMode = editTarget !== null;

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    loadEquipments(1, meta.size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Search & pagination ───────────────────────────────────────────────────

  const handleSearch = useCallback(() => {
    if (searchTerm.trim()) searchEquipments(searchTerm, 1, meta.size);
    else loadEquipments(1, meta.size);
  }, [searchTerm, meta.size, searchEquipments, loadEquipments]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    if (searchTerm.trim()) searchEquipments(searchTerm, newPage + 1, meta.size);
    else loadEquipments(newPage + 1, meta.size);
  }, [searchTerm, meta.size, searchEquipments, loadEquipments]);

  const handleRowsPerPageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(e.target.value, 10);
    if (searchTerm.trim()) searchEquipments(searchTerm, 1, newSize);
    else loadEquipments(1, newSize);
  }, [searchTerm, searchEquipments, loadEquipments]);

  // ── Form open/close ───────────────────────────────────────────────────────

  const openAddForm = useCallback(() => {
    setEditTarget(null);
    setFieldErrors({});
    setEquipmentFormOpen(true);
  }, []);

  const openEditForm = useCallback((equipment: EquipmentUnitFull) => {
    setEditTarget(equipment);
    setFieldErrors({});
    setEquipmentFormOpen(true);
  }, []);

  const closeEquipmentForm = useCallback(() => {
    setEquipmentFormOpen(false);
    setFieldErrors({});
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async (equipment: EquipmentUnit) => {
    try {
      const apiCall = isEditMode ? equipmentApi.updateEquipment : equipmentApi.createEquipment;
      await apiCall(equipment);
      enqueueSnackbar(`Equipment ${isEditMode ? 'updated' : 'created'} — ${equipment.displayName}`, { variant: 'success' });
      if (searchTerm.trim()) await searchEquipments(searchTerm, meta.page, meta.size);
      else await loadEquipments(meta.page, meta.size);
      closeEquipmentForm();
    } catch (err: any) {
      const { message, fieldErrors: apiFieldErrors } = extractApiError(err);
      setFieldErrors(apiFieldErrors ?? {});
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [isEditMode, searchTerm, meta.page, meta.size, searchEquipments, loadEquipments, closeEquipmentForm, enqueueSnackbar]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteRequest = useCallback((id: string | number) => {
    confirm('Are you sure you want to delete this equipment?', async () => {
      try {
        await equipmentApi.deleteEquipment(id as number);
        enqueueSnackbar('Equipment deleted successfully', { variant: 'success' });
        await loadEquipments(meta.page, meta.size);
      } catch (err) {
        enqueueSnackbar(`Failed to delete equipment: ${err}`, { variant: 'error' });
        setError(`Error deleting equipment: ${err}`);
      }
    });
  }, [confirm, enqueueSnackbar, loadEquipments, meta.page, meta.size, setError]);

  // ── Loading screen ────────────────────────────────────────────────────────

  if (loading && equipmentList.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, backgroundColor: theme.palette.background.default }}>
        <CircularProgress sx={{ color: '#667eea' }} />
        <Typography variant="h6" color="text.primary">Loading equipments…</Typography>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <Box sx={{ px: 2, mx: 'auto' }}>

        <PageHeader title="Equipment Management" onBack={() => navigate('/commondata')} backLabel="Back to Common Data" Icon={LocalShipping} />

        <StatusBanner error={error} status={null} />
        {canManageEquipment && (
          <>
            <StatusBanner error={null} status={uploadState.status} />
            {uploadState.error && <StatusBanner error={uploadState.error} status={null} />}
          </>
        )}

        {/* Bulk Upload */}
        {canManageEquipment && (
        <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="contained" component="label" startIcon={<FileUpload />} sx={gradientButtonSx}>
                Bulk Upload
                <input type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} />
              </Button>
              {uploadState.fileName && (
                <Typography variant="body2">Selected: <strong>{uploadState.fileName}</strong></Typography>
              )}
              <Button variant="contained" disabled={!uploadState.file || uploadState.uploading}
                onClick={handleUpload} sx={gradientButtonSx}>
                {uploadState.uploading ? 'Uploading…' : 'Upload'}
              </Button>
              <Button variant="outlined" sx={gradientButtonSx} onClick={() => equipmentApi.downloadTemplate()}>
                Download Template
              </Button>
            </Box>
            <UploadProgressBar uploading={uploadState.uploading} progress={uploadState.progress} />
          </CardContent>
        </Card>
        )}

        {/* Toolbar */}
        <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search by name, number, type or model…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearch} edge="end">
                        <Search sx={{ color: theme.palette.text.secondary }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary },
                }}
                sx={{
                  flexGrow: 1, minWidth: 250,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.divider },
                    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                    '&.Mui-focused fieldset': { borderColor: '#667eea' },
                  },
                  '& .MuiInputBase-input': { color: theme.palette.text.primary },
                }}
              />
              {canManageEquipment && (
                <>
                  <Button variant="contained" sx={gradientButtonSx} onClick={() => setTypesDialogOpen(true)}>
                    Equipment Types
                  </Button>
                  <Button variant="contained" sx={gradientButtonSx} onClick={() => setModelsDialogOpen(true)}>
                    Equipment Models
                  </Button>
                  <Button variant="contained" sx={gradientButtonSx} onClick={openAddForm}>
                    New Equipment
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Table */}
        <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" color="text.primary">Equipments ({meta.total})</Typography>
              <TablePagination component="div" count={meta.total} page={meta.page - 1}
                onPageChange={handlePageChange} rowsPerPage={meta.size}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 15, 20]} labelRowsPerPage="Per page" />
            </Box>

            <TableContainer component={Paper} variant="outlined"
              sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.background.paper }}>
                    {['Unit Number','Unit Name','Type','Model','Manufacturer','Capacity','Payload','Fuel Capacity','Status', ...(canManageEquipment ? ['Actions'] : [])].map((h) => (
                      <TableCell key={h} align={h === 'Actions' ? 'center' : 'left'}
                        sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equipmentList.length === 0
                    ? <EmptyTableRow colSpan={canManageEquipment ? 10 : 9} hasSearch={!!searchTerm.trim()} Icon={LocalShipping} />
                    : equipmentList.map((equipment) => (
                        <EquipmentRow key={equipment.id} equipment={equipment}
                          canManage={canManageEquipment}
                          onEdit={openEditForm} onDelete={handleDeleteRequest} />
                      ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination component="div" count={meta.total} page={meta.page - 1}
              onPageChange={handlePageChange} rowsPerPage={meta.size}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 15, 20]} labelRowsPerPage="Per page" />
          </CardContent>

          <ConfirmDialog open={confirmState.open} onClose={closeConfirm} onConfirm={executeConfirm}
            title="Confirm Delete" message={confirmState.message} />
        </Card>
      </Box>

      {/* Popups — each owns its own data */}
      {canManageEquipment && (
        <EquipmentTypeFormModel open={typesDialogOpen} onClose={() => setTypesDialogOpen(false)} />
      )}

      {canManageEquipment && (
        <ManageEquipmentModelsPopUp open={modelsDialogOpen} onClose={() => setModelsDialogOpen(false)} />
      )}

      {canManageEquipment && <EquipmentFormModel
        open={equipmentFormOpen}
        onClose={closeEquipmentForm}
        onSubmit={handleSubmit}
        equipment={editTarget ?? undefined}
        isEditMode={isEditMode}
        errorMessages={fieldErrors}
      />}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// EquipmentRow
// ---------------------------------------------------------------------------

interface EquipmentRowProps {
  equipment: EquipmentUnitFull;
  canManage: boolean;
  onEdit: (e: EquipmentUnitFull) => void;
  onDelete: (id: string | number) => void;
}

const EquipmentRow: React.FC<EquipmentRowProps> = React.memo(({ equipment, canManage, onEdit, onDelete }) => {
  const theme: Theme = useTheme();
  const cell = { borderBottom: 'none' };

  return (
    <TableRow hover sx={{ '&:hover': { backgroundColor: theme.palette.background.paper }, borderBottom: `1px solid ${theme.palette.divider}` }}>
      <TableCell sx={cell}><Typography variant="subtitle2" color="text.primary">{equipment.code}</Typography></TableCell>
      <TableCell sx={cell}><Typography variant="subtitle2" color="text.primary">{equipment.displayName}</Typography></TableCell>
      <TableCell sx={cell}><Typography color="text.secondary">{equipment.typeName}</Typography></TableCell>
      <TableCell sx={cell}><Typography color="text.secondary">{equipment.equipmentModel}</Typography></TableCell>
      <TableCell sx={cell}><Typography color="text.secondary">{equipment.manufacturer}</Typography></TableCell>
      <TableCell sx={cell}><Typography variant="body2" color="text.secondary">{equipment.capacityM3 ? `${equipment.capacityM3} m³` : '—'}</Typography></TableCell>
      <TableCell sx={cell}><Typography variant="body2" color="text.secondary">{equipment.payloadT ? `${equipment.payloadT} t` : '—'}</Typography></TableCell>
      <TableCell sx={cell}><Typography variant="body2" color="text.secondary">{equipment.fuelCapacity ? `${equipment.fuelCapacity} l` : '—'}</Typography></TableCell>
      <TableCell sx={cell}>
        <Chip label={equipment.isActive ? 'Active' : 'Inactive'} size="small" color={equipment.isActive ? 'success' : 'default'} />
      </TableCell>
      {canManage && (
        <TableCell align="center" sx={cell}>
          <IconButton size="small" sx={{ color: '#06beb6' }} onClick={() => onEdit(equipment)} aria-label={`Edit ${equipment.displayName}`}>
            <Edit />
          </IconButton>
          <IconButton size="small" sx={{ color: '#ef4444' }} onClick={() => onDelete(equipment.id)} aria-label={`Delete ${equipment.displayName}`}>
            <Delete />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
});

EquipmentRow.displayName = 'EquipmentRow';

export default EquipmentsPage;